import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Facebook, Menu, X, Info, PenTool, Layers, ClipboardCheck } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  Constantin Design & Project Management — concept site. Design/architecture-led
  identity: editorial, minimal, built around the studio's own branded design
  renderings and one real jobsite photo.
*/

const CHARCOAL = "#1E1E1C";
const SAND = "#C9A97B";
const CREAM = "#F7F5F1";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/constantindesignbuild/";
const NAME = "Constantin Design & Project Management";
const SLUG = "constantin-design-build-concept";
const P = "/construction/constantin";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function ConstantinConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Studio", "#studio"], ["Design Work", "#work"], ["Services", "#services"], ["Client Reviews", "#reviews"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Jost', sans-serif" }} data-testid="constantin-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600&family=Playfair+Display:wght@500;600;700&display=swap');
        .ct-serif { font-family: 'Playfair Display', serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-white transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.06)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="ct-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.png`} alt={NAME} className="h-10 w-10 object-contain" />
            <span className="ct-serif text-lg" style={{ color: CHARCOAL }}>Constantin</span>
          </a>
          <div className="hidden lg:flex items-center gap-9">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-light tracking-wide" style={{ color: `${INK}99` }}>{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="ct-header-cta" className="hidden sm:inline-flex px-5 py-2.5 text-sm font-medium tracking-wide text-white" style={{ background: CHARCOAL }}>
            Book a Consultation
          </a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="ct-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-light">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="ct-mobile-cta" className="block mt-2 text-center px-5 py-3 text-sm font-medium text-white" style={{ background: CHARCOAL }}>Book a Consultation</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center" data-testid="ct-hero">
        <img src={`${P}/poolvilla-render.jpg`} alt="A Constantin Design & Project Management design concept" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(270deg, rgba(30,30,28,.9) 0%, rgba(30,30,28,.7) 46%, rgba(30,30,28,.08) 82%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div {...fadeUp} className="max-w-xl ml-auto border-r-4 p-6 sm:p-8 text-right bg-black/45 backdrop-blur-[1px]" style={{ borderColor: SAND }}>
            <span className="text-xs font-medium uppercase tracking-[0.3em]" style={{ color: SAND }}>Davao City &middot; Design &amp; Project Management</span>
            <h1 className="mt-5 text-5xl sm:text-6xl leading-[1.08] text-white ct-serif font-medium">Design First. Build With Intent.</h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-lg font-light">
              Architectural design, interior concepts and project management, carried from first sketch through construction oversight.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-end gap-4">
              <a href="#contact" data-testid="ct-hero-cta" className="px-7 py-3.5 text-sm font-medium tracking-wide text-white" style={{ background: SAND, color: CHARCOAL }}>Book a Consultation</a>
              <a href="#work" data-testid="ct-hero-work" className="border border-white/40 px-7 py-3.5 text-sm font-medium tracking-wide text-white hover:bg-white/10 transition-colors">See Our Design Work</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STUDIO */}
      <section id="studio" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 md:py-28 text-center">
        <motion.div {...fadeUp}>
          <span className="text-xs font-medium uppercase tracking-[0.3em]" style={{ color: SAND }}>The Studio</span>
          <h2 className="mt-4 text-4xl sm:text-5xl ct-serif font-medium leading-snug">Design &amp; Project Management, Together</h2>
          <p className="mt-6 text-lg leading-relaxed font-light" style={{ color: `${INK}90` }}>
            We work across architectural design, interior concepts and project management — keeping design intent intact from first concept through construction.
          </p>
        </motion.div>
      </section>

      {/* DESIGN WORK */}
      <section id="work" className="py-20 md:py-28" style={{ background: CHARCOAL }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-medium uppercase tracking-[0.3em]" style={{ color: SAND }}>Design Work</span>
            <h2 className="mt-4 text-4xl sm:text-5xl text-white ct-serif font-medium leading-snug">Concepts &amp; Renderings</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { img: `${P}/interior-render.jpg`, label: "Interior design concept" },
              { img: `${P}/exterior-renders.jpg`, label: "Exterior design concepts" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="ct-work-item" className="overflow-hidden">
                <img src={it.img} alt={it.label} className="w-full h-72 object-cover" loading="lazy" />
                <p className="mt-3 text-sm text-white/60 font-light">{it.label}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-10 overflow-hidden">
            <img src={`${P}/jobsite-real.jpg`} alt="A Constantin project under construction" className="w-full h-72 object-cover" loading="lazy" />
            <p className="mt-3 text-sm text-white/60 font-light">A design brought to life — project under construction</p>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-medium uppercase tracking-[0.3em]" style={{ color: SAND }}>How We Help</span>
          <h2 className="mt-4 text-4xl sm:text-5xl ct-serif font-medium leading-snug">Services</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [PenTool, "Architectural & Interior Design", "Design concepts developed around how the space will actually be used."],
            [Layers, "Design-and-Build", "Design intent carried through into construction, not handed off and lost."],
            [ClipboardCheck, "Project Management", "Oversight through the build so the finished result matches the design."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: SAND }} />
              <h3 className="mt-4 text-lg font-medium ct-serif">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed font-light" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ATTRIBUTABLE CUSTOMER REVIEWS */}
      <section id="reviews" className="py-20 md:py-28" style={{ background: CHARCOAL }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <span className="text-xs font-medium uppercase tracking-[0.3em]" style={{ color: SAND }}>Client Perspectives</span>
            <h2 className="mt-4 text-4xl sm:text-5xl text-white ct-serif font-medium leading-snug">What Clients Have Shared</h2>
            <p className="mt-4 text-white/60 font-light">Public Google listing snapshot: 4.7/5 from 22 reviews.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              ["Iannicar R.", "September 2018", "Praised the team's professionalism, attentive listening, quality and effort throughout the service."],
              ["Angelo R.", "March 2019", "Thanked the wider project team for their support and for helping bring the client's project to life."],
              ["Paulo R.", "February 2018", "Highlighted the studio's design quality, craftsmanship and trustworthiness as a contractor."],
              ["Carlo C.", "September 2018", "Described the company as highly professional and especially trustworthy."],
            ].map(([name, date, review]) => (
              <motion.article key={name} {...fadeUp} className="border border-white/10 p-6">
                <p className="text-white/80 leading-relaxed font-light">{review}</p>
                <p className="mt-5 text-sm font-medium" style={{ color: SAND }}>{name} <span className="text-white/40 font-light">· {date}</span></p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: "#EDE8DE" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-medium uppercase tracking-[0.3em]" style={{ color: SAND }}>Get In Touch</span>
            <h2 className="mt-4 text-4xl ct-serif font-medium leading-snug">Book a Consultation</h2>
            <ul className="mt-8 space-y-6 font-light">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: SAND }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Studio</div><div>Davao City, Philippines</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: SAND }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="ct-facebook-link" className="hover:underline">facebook.com/constantindesignbuild</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="ct"
              accent={SAND}
              accentText={CHARCOAL}
              heading="Book a Consultation"
              subheading="Tell us about the space you're designing — our team will follow up to schedule a consultation."
              projectTypes={["Architectural Design", "Interior Design", "Design-and-Build", "Project Management"]}
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: CHARCOAL, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.png`} alt={NAME} className="h-9 w-9 object-contain" />
              <span className="text-white ct-serif text-lg">Constantin</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed font-light">Design &amp; project management studio based in Davao City.</p>
          </div>
          <div>
            <div className="text-white text-sm font-medium mb-3">Contact</div>
            <p className="text-sm font-light">Davao City, Philippines</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs font-light">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the studio.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-medium tracking-wide text-white shadow-xl" style={{ background: CHARCOAL }}>
        Message Constantin
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
